import * as breakpoint from '@pixelunion/breakpoint';
import EventHandler from '@pixelunion/events';
import Flickity from 'flickity';

// This class is intended to be used across the site whenever we want to
// have a carousel for a section that is only on mobile. It improves on
// Flickity to make the carousel more accessible, and also keeps consistency
// across the theme.

export default class MobileCarousel {
  constructor(settings) {
    this.carouselWrapper = settings.carouselWrapper;
    this.slideSelector = settings.slideSelector;

    this.slides = this.carouselWrapper.querySelectorAll(this.slideSelector);

    if (this.slides.length <= 1) {
      return;
    }

    this.events = new EventHandler();

    this.flickity = null;
    this.rimgEvent = null;
    this.pagination = null;
    this.paginationButtons = [];
    this.paginationEvents = [];
    this.paginationKeydown = false;

    this.events.register(window, 'resize', () => {
      // On screen resize, we want to enable flickity only when below the M breakpoint.
      // If flickity is enabled already, we don't want to set it up again.
      if (this.flickity || breakpoint.min('M')) {
        if (this.flickity && breakpoint.min('M')) {
          this._disableFlickity();
        }
      } else {
        this._setupFlickity();
      }
    });

    // Initial setup of flickity if we are below the M breakpoint
    if (!breakpoint.min('M')) {
      this._setupFlickity();
    }
  }

  _disableFlickity() {
    this.flickity.destroy();
    this.flickity = null;
    this.events.unregister(this.rimgEvent);

    this.paginationEvents.forEach(event => this.events.unregister(event));
    this.carouselWrapper.removeChild(this.pagination);
  }

  _setupFlickity() {
    this.flickity = new Flickity(this.carouselWrapper, {
      accessibility: false, // Flickity's inherent "accessibility" actually makes it worse
      adaptiveHeight: true,
      autoPlay: false,
      cellAlign: 'left',
      cellSelector: this.slideSelector,
      contain: true,
      pageDots: false,
      prevNextButtons: false,
      selectedAttraction: 0.2,
      friction: 0.8,
      wrapAround: true,
    });

    this.rimgEvent = this.events.register(this.carouselWrapper, 'rimg:load', () => {
      this.flickity.resize();
    });

    this._createPagination();
    this._updateTabindex();

    this.flickity.on('change', () => {
      this._updatePagination();
      this._updateTabindex();
    });

    this.flickity.on('settle', index => {
      if (this.paginationKeydown) {
        this.slides[index].focus();
        this.paginationKeydown = false;
      }
    });
  }

  // We have to add in our own pagination buttons since the ones in flickity cannot be clicked
  // through keyboard navigation. This replicates the creation of them and their behaviour.
  _createPagination() {
    const mobilePagination = document.createElement('ol');
    mobilePagination.classList.add('mobile-carousel__pagination');

    this.paginationEvents = [];
    this.paginationButtons = [];

    for (let i = 0; i < this.slides.length; i++) {
      this.slides[i].setAttribute('tabindex', '-1');
      this.slides[i].setAttribute('aria-label', `Carousel slide ${i + 1}`);

      const dot = document.createElement('li');
      dot.classList.add('mobile-carousel__pagination-item');

      const button = document.createElement('button');
      button.classList.add('mobile-carousel__pagination-button');
      button.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      button.dataset.selected = i === 0;

      const span = document.createElement('span');
      span.classList.add('visually-hidden');
      span.innerHTML = `Go to slide ${i + 1}`;

      button.appendChild(span);
      dot.appendChild(button);
      mobilePagination.appendChild(dot);

      const buttonEvent = this.events.register(button, 'click', () => {
        this.flickity.select(i, true, false);
      });

      const keydownEvent = this.events.register(button, 'keydown', () => {
        this.paginationKeydown = true;
      });

      this.paginationButtons.push(button);
      this.paginationEvents.push(buttonEvent);
      this.paginationEvents.push(keydownEvent);
      this.pagination = mobilePagination;
    }

    this.carouselWrapper.appendChild(mobilePagination);
  }

  _updatePagination() {
    for (let i = 0; i < this.paginationButtons.length; i++) {
      this.paginationButtons[i].dataset.selected = 'false';
      this.paginationButtons[i].setAttribute('aria-selected', 'false');
    }
    this.paginationButtons[this.flickity.selectedIndex].dataset.selected = 'true';
    this.paginationButtons[this.flickity.selectedIndex].setAttribute('aria-selected', 'true');
  }

  _updateTabindex() {
    // We need to remove tabindex from the slides that are not currently
    // selected, so that slides not currently shown cannot receive keyboard focus.
    for (let i = 0; i < this.slides.length; i++) {
      const links = this.slides[i].querySelectorAll('a');
      for (let j = 0; j < links.length; j++) {
        links[j].setAttribute('tabindex', i === this.flickity.selectedIndex ? '0' : '-1');
      }
    }
  }

  unload() {
    this.events.unregisterAll();

    if (this.flickity) {
      this._disableFlickity();
    }
  }
}
